<template>
  <WdsAlert v-if="errorBannerMessage" error class="m-b-15">{{ errorBannerMessage }}</WdsAlert>

  <WdsStack direction-column gap-lg>
    <div class="tw-grid tw-grid-cols-2 tw-gap-4">
      <WdsInput
        v-model="name.firstName"
        :disabled="nameInputDisabled || loading"
        :label="$t('First name') + ' *'"
        :placeholder="$t('First name')"
        :error="errors.firstName"
        data-test="tc-first-name"
        required
      />

      <WdsInput
        v-model="name.middleName"
        :disabled="nameInputDisabled || loading"
        :label="$t('Middle name')"
        :placeholder="$t('Middle name')"
        :error="errors.middleName"
        data-test="tc-middle-name"
      />

      <WdsInput
        v-model="name.lastName"
        :disabled="nameInputDisabled || loading"
        :label="$t('Last name') + ' *'"
        :placeholder="$t('Last name')"
        :error="errors.lastName"
        data-test="tc-last-name"
        required
      />

      <WdsInput
        v-model="email"
        :disabled="emailInputDisabled || loading"
        :label="$t('E-mail') + ' *'"
        :placeholder="$t('E-mail address')"
        :error="errors.email"
        data-test="tc-mail"
        required
      />

      <template v-if="!isEditing || (isEditing && country?.value)">
        <WdsText class="tw-col-span-2">
          {{ $t('Location') }}

          <template #subText>
            {{
              $t(
                'The worker’s location ensures the right compliance rules are applied from the start. We specifically recommend adding it for global hires.',
              )
            }}
          </template>
        </WdsText>

        <WdsSelect
          v-model="country"
          :disabled="countryInputDisabled || loading"
          :list="getCountries"
          :placeholder="$t('Select country')"
          :error="errors.country"
          searchable
          :can-deselect="!countryInputDisabled"
          :class="{
            'tw-col-span-2': !countryHasStates,
          }"
          required
          name="tc-country-select"
          data-test="tc-country-select"
        />

        <WdsSelect
          v-if="countryHasStates"
          v-model="state"
          :disabled="stateInputDisabled || loading"
          :list="getStates"
          :placeholder="$t('Select state')"
          :error="errors.state"
          searchable
          :can-deselect="!stateInputDisabled"
          name="tc-state-select"
          data-test="tc-state-select"
        />

        <WdsAlert icon warning class="tw-col-span-2">
          {{
            $t(
              'If you go on to hire this worker immediately (invite and hire), the worker won’t be able to change their country when they sign up.',
            )
          }}
        </WdsAlert>
      </template>
    </div>

    <WdsDivider align-left class="tw-my-2">
      <WdsLabel subtle xs>
        <WdsStack direction-row gap-xs align-center>
          <WdsIcon name="locked" size="12" />
          <span>{{ $t('Not shown to the worker') }}</span>
        </WdsStack>
      </WdsLabel>
    </WdsDivider>

    <WdsStack direction-column gap-sm>
      <WdsText :text="$t('Skills')" />

      <TypeSelect
        name="skills"
        tag-type="skills"
        :placeholder="$t('Add skills')"
        :selected-in="skills"
        :show-typesuggestions="false"
        :can-add-unsupported-types="true"
        data-test="tc-skills-input"
      />
    </WdsStack>

    <WdsStack direction-column gap-sm>
      <WdsStack direction-row justify-space-between align-center>
        <WdsText :text="$t('External ID')" />

        <WdsButtonGhost
          primary
          data-test="tc-link-add"
          :disabled="loading || displayExternalIdentifierInput"
          @click="addExternalIdentifier"
        >
          <WdsStack align-center gap-sm>
            <WdsIcon name="plus" size="18" />
            {{ $t('Add ID') }}
          </WdsStack>
        </WdsButtonGhost>
      </WdsStack>

      <WdsInput
        v-if="displayExternalIdentifierInput"
        v-model="externalIdentifier"
        :disabled="loading"
        :data-test="`tc-external-identifier-input`"
        :placeholder="$t('Add external ID')"
      />
    </WdsStack>

    <WdsStack direction-column gap-sm>
      <WdsStack direction-row justify-space-between align-center>
        <WdsText :text="$t('Links')" />

        <WdsButtonGhost primary data-test="tc-link-add" :disabled="loading" @click="addLink('')">
          <WdsStack align-center gap-sm>
            <WdsIcon name="plus" size="18" />
            {{ links.length === 0 ? $t('Add link') : $t('Add another link') }}
          </WdsStack>
        </WdsButtonGhost>
      </WdsStack>

      <template v-if="links.length > 0">
        <div v-for="(link, index) in links" :key="link.key">
          <WdsStack align-start gap-sm>
            <div class="tw-grow">
              <WdsInput
                v-model="links[index].url"
                :disabled="loading"
                :error="errors?.[`links.${index}`]"
                :data-test="`tc-link-input-${index}`"
                prefix-icon="link"
              />
            </div>

            <WdsButtonGhost
              class="!tw-mt-2 tw-pt"
              :data-test="`tc-link-remove-${index}`"
              @click="removeLink(index)"
            >
              <WdsIcon name="close" size="16" class="tw-text-gray-600" />
            </WdsButtonGhost>
          </WdsStack>
        </div>
      </template>
    </WdsStack>

    <WdsStack direction-column gap-sm>
      <WdsStack direction-row justify-space-between align-center>
        <WdsText :text="$t('Attachments')" />

        <PickerFileFilebank
          v-slot="{ openFilePicker }"
          :exclude-ids="attachments.map(({ id }) => id)"
          use-global-id
          @files-selected="addAttachments"
        >
          <WdsButtonGhost
            primary
            :disabled="loading"
            class="tw-flex tw-items-center tw-gap-x-2"
            data-test="tc-file-add"
            @click="openFilePicker"
          >
            <WdsIcon name="plus" size="18" />
            {{ !attachments.length ? $t('Add file') : $t('Add another file') }}
          </WdsButtonGhost>
        </PickerFileFilebank>
      </WdsStack>

      <WdsStack v-for="attachment in attachments" :key="attachment.id" align-center gap-sm>
        <WdsIcon name="document" size="16" class="tw-shrink-0" />

        <a
          :href="laravelNamedRoute(namedRoutes.files.download, { fileId: attachment.id })"
          class="tw-font-medium tw-grow tw-line-clamp-1 tw-break-all"
          :data-test="`tc-file-${attachments.indexOf(attachment)}`"
          target="_blank"
          :title="attachment.name"
        >
          {{ attachment.name }}
        </a>

        <WdsButtonGhost
          :data-test="`tc-file-remove-${attachments.indexOf(attachment)}`"
          @click="removeAttachment(attachment.id)"
        >
          <WdsIcon name="close" size="16" class="tw-text-gray-600" />
        </WdsButtonGhost>
      </WdsStack>
    </WdsStack>

    <WdsAlert v-show="errors.customFieldValues" error>
      {{ errors.customFieldValues }}
    </WdsAlert>

    <FieldableFormCustomFields
      v-model:field-values="customFieldValues"
      :context-message="
        $t('Details specified below will only be visible to you and your team members.')
      "
      :account-id="currentAccountId"
      :fieldable-type="TypeSupportingCustomFieldValues.TrustedContact"
      :field-value-fragments="initialCustomFieldValues"
      disable-worker-input-fields
    />

    <WdsStack v-if="workerCustomFieldValues.length > 0" direction-column gap-sm>
      <WdsDivider align-left class="tw-my-2">
        <WdsLabel subtle xs>
          <WdsStack direction-row gap-xs align-center>
            <WdsIcon name="user" size="12" />
            <span>{{ $t('Additional information') }}</span>
          </WdsStack>
        </WdsLabel>
      </WdsDivider>

      <WdsText secondary>
        {{ $t('These fields are filled in by the worker and cannot be edited.') }}
      </WdsText>

      <table class="tw-table-fixed tw-w-full">
        <thead>
          <tr class="tw-bg-true-gray-50 tw-rounded tw-text-gray-600">
            <th class="tw-font-normal tw-py-1 tw-px-2 tw-rounded-l-lg">{{ $t('Field') }}</th>
            <th class="tw-font-normal tw-py-1 tw-px-2 tw-rounded-r-lg">{{ $t('Value') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(field, index) in workerCustomFieldValues"
            :key="index"
            class="tw-border-b tw-border-r-0 tw-border-l-0 tw-border-solid tw-border-gray-100"
          >
            <td class="tw-w-1/2 tw-py-1 tw-px-2">{{ field.title }}</td>
            <td class="tw-w-1/2 tw-py-1 tw-px-2">{{ field.displayValue || '–' }}</td>
          </tr>
        </tbody>
      </table>
    </WdsStack>

    <WdsStack v-if="!messageInputHidden" direction-column gap-lg>
      <WdsDivider />

      <WdsToggle v-model:checked="showMessageInput" sm data-test="tc-message-toggle">
        <WdsStack align-center gap-xs>
          <span>{{ $t('Add custom message') }}</span>
          <WdsTag sm>{{ $t('Optional') }}</WdsTag>
        </WdsStack>
      </WdsToggle>

      <WdsInputArea
        v-if="showMessageInput"
        v-model="message"
        :placeholder="$t('Share a message with the worker...')"
        :error="errors.message"
        data-test="tc-message-input"
        height="60px"
      />
    </WdsStack>
  </WdsStack>

  <WdsStack justify-space-between class="tw-mt-7">
    <WdsButton tertiary data-test="tc-manage-cancel" @click="onCancel">
      {{ $t('Cancel') }}
    </WdsButton>

    <WdsStack align-center gap-md>
      <WdsSpinner v-if="loading" indeterminate />

      <WdsButton
        v-if="isEditing"
        primary
        data-test="tc-save"
        :disabled="loading"
        @click="handleOnConfirm('update')"
      >
        {{ $t('Save') }}
      </WdsButton>

      <WdsTooltip v-if="!isEditing">
        <WdsButton
          secondary
          data-test="tc-invite-and-offer"
          :disabled="loading"
          @click="handleOnConfirm('inviteAndOffer')"
        >
          {{ $t('Invite and hire') }}
        </WdsButton>

        <template #tooltip-content>
          {{
            $t(
              'The worker will be invited to create their Worksome account now, however they won’t see the details of the hire until it is offered to them.',
            )
          }}
        </template>
      </WdsTooltip>

      <WdsButton
        v-if="!isEditing"
        primary
        data-test="tc-invite-submit"
        :disabled="loading"
        @click="handleOnConfirm('inviteOnly')"
      >
        {{ $t('Invite') }}
      </WdsButton>
    </WdsStack>
  </WdsStack>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import {
  WdsAlert,
  WdsStack,
  WdsButton,
  WdsButtonGhost,
  WdsSpinner,
  WdsIcon,
  WdsText,
  WdsInput,
  WdsInputArea,
  WdsDivider,
  WdsLabel,
  WdsTag,
  WdsSelect,
  WdsTooltip,
  WdsToggle,
} from '@worksome/wds-components'

import TypeSelect from '@/components/TypeSelect.vue'
import PickerFileFilebank from '@/components/PickerFileFilebank.vue'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { FormValidation } from '@/utils/formValidation'
import FieldableFormCustomFields from '@/components/Fieldable/FieldableFormCustomFields.vue'
import { TypeSupportingCustomFieldValues } from '@/gql/graphql'
import {
  diffCustomFieldValueInputs,
  mapCustomFieldValueToCustomFieldValueInput,
} from '@/components/Fieldable/utils'
import { useTranslation } from '@/composables/translation'
import { useLocation } from '@/composables/useLocation'

const props = defineProps({
  onCancel: {
    type: Function,
    required: true,
  },
  onConfirm: {
    type: Function,
    required: true,
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
  initialName: {
    type: Object,
    default: () => ({
      firstName: '',
      middleName: '',
      lastName: '',
    }),
    validator: (obj) =>
      ['firstName', 'middleName', 'lastName'].every(
        (key) => !!obj[key] && typeof obj[key] === 'string',
      ),
  },
  initialEmail: {
    type: String,
    default: '',
  },
  initialCountry: {
    type: [String, null],
    default: null,
  },
  initialState: {
    type: [String, null],
    default: null,
  },
  initialLinks: {
    type: Array,
    default: () => [],
  },
  initialAttachments: {
    type: Array,
    default: () => [],
  },
  initialSkills: {
    type: Array,
    default: () => [],
  },
  initialExternalIdentifier: {
    type: String,
    default: '',
  },
  initialCustomFieldValues: {
    type: Array,
    default: () => [],
  },
  workerCustomFieldValues: {
    type: Array,
    default: () => [],
  },
  nameInputDisabled: {
    type: Boolean,
    default: false,
  },
  emailInputDisabled: {
    type: Boolean,
    default: false,
  },
  countryInputDisabled: {
    type: Boolean,
    default: false,
  },
  stateInputDisabled: {
    type: Boolean,
    default: false,
  },
  messageInputHidden: {
    type: Boolean,
    default: false,
  },
})

const { $t } = useTranslation()

const { getCountries, getStates, setSelectedCountry, setSelectedState, countryHasStates } =
  useLocation()

const currentAccountId = ref(currentAccount.globalId)

const loading = ref(false)

const errorBannerMessage = ref(null)
const errors = ref({})

const name = ref(props.initialName)
const email = ref(props.initialEmail)
const country = ref(props.initialCountry ? { value: props.initialCountry } : null)
const state = ref(props.initialState ? { value: props.initialState } : null)
const links = ref(props.initialLinks)
const attachments = ref(props.initialAttachments)
const skills = ref(props.initialSkills)
const externalIdentifier = ref(props.initialExternalIdentifier)
const customFieldValues = ref(
  mapCustomFieldValueToCustomFieldValueInput(props.initialCustomFieldValues),
)
const showMessageInput = ref(false)
const message = ref(null)

// Pristine baseline of the stored values, kept apart from the editable ref.
const initialCustomFieldValueInputs = computed(() =>
  mapCustomFieldValueToCustomFieldValueInput(props.initialCustomFieldValues),
)

// Only the values the user changed in this form. Submitting untouched fields
// would delete their stored values whenever the form holds them as empty.
const changedCustomFieldValues = computed(() =>
  diffCustomFieldValueInputs(customFieldValues.value, initialCustomFieldValueInputs.value),
)

const resetFields = () => {
  name.value = props.initialName
  email.value = props.initialEmail
  country.value = props.initialCountry ? { value: props.initialCountry } : null
  state.value = props.initialState ? { value: props.initialState } : null
  links.value = props.initialLinks
  attachments.value = props.initialAttachments
  skills.value = props.initialSkills
  externalIdentifier.value = props.initialExternalIdentifier
  customFieldValues.value = mapCustomFieldValueToCustomFieldValueInput(
    props.initialCustomFieldValues,
  )
  message.value = ''
}

const resetErrors = () => {
  errors.value = {}
  errorBannerMessage.value = null
}

const hasSubmitted = ref(false)

const externalIdentifierIsManuallyToggled = ref(false)
const displayExternalIdentifierInput = computed(
  () => externalIdentifier.value || externalIdentifierIsManuallyToggled.value,
)
const addExternalIdentifier = () => {
  externalIdentifierIsManuallyToggled.value = true
}

const addLink = (url) => {
  const hasEmptyRows = links.value.some((link) => link.url === '')

  if (!hasEmptyRows) {
    links.value = [...links.value, { key: links.value.length, url }]
  }
}

const removeLink = (linkIndex) => {
  links.value.splice(linkIndex, 1)

  delete errors.value?.[`input.links.${linkIndex}`]
}

const addAttachments = (selectedAttachments) => {
  attachments.value = [
    ...attachments.value,
    ...selectedAttachments.map((selectedAttachment) => ({
      name: selectedAttachment.name,
      id: selectedAttachment.id,
      storageName: selectedAttachment.storageName,
    })),
  ]
}

const removeAttachment = (attachmentId) => {
  attachments.value.splice(
    attachments.value.map((attachment) => attachment.id).indexOf(attachmentId),
    1,
  )
}

async function handleOnConfirm(action) {
  loading.value = true

  await props
    .onConfirm(
      {
        name,
        email,
        country,
        state,
        links,
        attachments,
        skills,
        message,
        externalIdentifier,
        customFieldValues: changedCustomFieldValues,
      },
      action,
    )
    .then(() => {
      resetFields()
      resetErrors()
    })
    .catch((error) => {
      hasSubmitted.value = true
      resetErrors()

      const { graphQLErrors } = error

      if (FormValidation.containsFormValidations(graphQLErrors)) {
        errors.value = FormValidation.getSingularGraphqlErrors(graphQLErrors, 'input.')
      } else if (FormValidation.containsErrorMessage(graphQLErrors)) {
        errorBannerMessage.value = graphQLErrors[0].message
      } else {
        errorBannerMessage.value = $t('An error occurred')
      }
    })
    .finally(() => {
      loading.value = false
    })
}

watch(
  () => country.value,
  (selected) => {
    setSelectedCountry(selected?.value?.toUpperCase() ?? '')

    // In case the user deselects the country, we need to reset the state
    if (selected === null) {
      state.value = null
    }
  },
  { immediate: true },
)

watch(
  () => state.value,
  (selected) => {
    setSelectedState(selected?.value?.toUpperCase() ?? '')
  },
  { immediate: true },
)
</script>

<style scoped lang="scss">
:deep(label) {
  @apply tw-mb-0;
}
</style>
